import { useCampaignClient, useIncentiveClient, useUserClient } from '@/lib/api'
import { CACHE_KEYS, invalidateIncentiveQueries } from '@/lib/cache-utils'
import {
  CreateIncentiveItemApiSchema,
  UpdateIncentiveItemApiSchema,
  createIncentiveItemFormSchema,
  updateIncentiveItemFormSchema,
  type IncentiveItemFormType,
} from '@/schema/incentive.schema'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

// Define API response types locally (not part of form schema)
type Customer = {
  id: string
  name: string
  integrations?: Integration[]
}

type Integration = {
  id: string
  type: string
  name: string
  features?: Record<string, unknown>
}

type IncentiveCategory = {
  id: string
  name: string
}

type VoucherFile = {
  fileType: string
  s3Key: string
  url?: string | URL
}

type IncentiveItemResponse = {
  id?: string
  name?: string
  price?: string | number
  description?: string
  categoryId?: string
  type?: 'unique' | 'universal' | 'physical' | 'intersolve' | 'gift_group' | ''
  skuCode?: string
  digitalCode?: string
  integrationId?: string
  isActive?: boolean
  picture?: string
  voucherCodesFilePath?: string
  customerId?: string
  isClaimed?: boolean
  sku?: string
  isAttachedToLotteryCampaign?: boolean
  incentiveAttachedToCampaign?: number
  files?: VoucherFile[]
  groupChildren?: { childItemId: string }[]
}

type UserInfo = {
  role: string
  customerId?: string
}

/**
 * Turn a non-2xx ts-rest response into a thrown error.
 *
 * `.mutate` resolves for *any* status unless the client sets
 * `throwOnUnknownStatus`, which ours does not. So a 400 from a server guard
 * used to land in `onSuccess`: the form navigated away and the admin saw a
 * save that never happened. `campaign/list.tsx` already does this check; the
 * body is stringified so `handleError` can JSON.parse it back out.
 */
function throwIfNotOk<T extends { status: number; body: unknown }>(res: T): T {
  if (res.status >= 400) throw new Error(JSON.stringify(res.body))
  return res
}
import {
  ArrowLeft,
  Download,
  DownloadSimple,
  FileCsv,
  Trash,
} from '@phosphor-icons/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import cx from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useMatch, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../button'
import ErrorModal from '../error-modal'
import CustomFileUpload from '../form/custom-file-upload'
import RadioGroupWithDescription from '../form/custom-radio-group-with-description'
import { CustomSwitchComponent } from '../form/custom-switch'
import CustomTextarea from '../form/custom-textarea'
import { FormInput } from '../form-fields'
import GiftGroupSettings from './custom-properties/gift-group-settings'
import IntersolveSettings from './custom-properties/intersolve'
import { incentiveTypeOptions } from './incentive-type-options'

type CustomersWithIntegrations = Customer & { integrations: Integration[] }

const customerHasIntersolve = (
  customers: CustomersWithIntegrations[] | undefined,
  customerId: string | undefined,
): boolean =>
  !!customers?.some(
    (c) =>
      c.id === customerId &&
      c?.integrations?.some(
        (i) => i?.type === 'INTERSOLVE' || i?.type === 'INTERSOLVE_CLASSIC',
      ),
  )

function uuidv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c == 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export const IncentiveForm = ({
  onCloseModal,
  incentiveByModal,
}: {
  incentiveByModal?: IncentiveItemFormType & { id?: string }
  onCloseModal?: () => void
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id, incentiveId: urlIncentiveId } = useParams()
  const [incentiveId, setIncentiveId] = useState<string | undefined>(
    urlIncentiveId,
  )
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState<{
    title: string
    text: string
  }>()
  const [showDisableModal, setShowDisableModal] = useState(false)

  // Initialize incentive ID
  useEffect(() => {
    if (!incentiveId) {
      setIncentiveId(urlIncentiveId || uuidv4())
    }
  }, [incentiveId, urlIncentiveId])

  // Fetch data
  const incentiveClient = useIncentiveClient()
  const campaignClient = useCampaignClient()
  const userClient = useUserClient()
  const queryClient = useQueryClient()

  const {
    data: incentiveResponse,
    isLoading: loadingIncentive,
    refetch: getIncentiveById,
  } = useQuery({
    queryKey: ['incentiveById', incentiveId],
    queryFn: async () => {
      if (!incentiveId) return null
      return await incentiveClient.getById.query({
        params: { id: incentiveId },
      })
    },
    enabled: !!urlIncentiveId && !!incentiveId,
  })

  const incentiveById = useMemo(
    () =>
      (incentiveResponse?.body ?? incentiveByModal) as
        | IncentiveItemResponse
        | undefined,
    [incentiveResponse?.body, incentiveByModal],
  )

  const { data: customersResponse, isLoading: customersLoading } = useQuery({
    queryKey: [CACHE_KEYS.CUSTOMERS],
    queryFn: async () => {
      const response = await campaignClient.customers.query()
      return response.status === 200 ? response.body : []
    },
  })

  const customersData = useMemo(
    () => ({
      customers: (customersResponse ?? []) as CustomersWithIntegrations[],
    }),
    [customersResponse],
  )

  const { data: userResponse, isLoading: loadingUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => await userClient.getMe.query(),
  })

  const user = useMemo(
    () => ({ user: userResponse?.body as UserInfo | undefined }),
    [userResponse?.body],
  )

  const { data: categoriesResponse, isLoading: categoriesLoading } = useQuery({
    queryKey: ['incentiveCategories'],
    queryFn: async () => await incentiveClient.getCategories.query(),
  })

  const categoriesData = useMemo(
    () => ({
      incentiveCategories: (categoriesResponse?.status === 200
        ? categoriesResponse.body
        : []) as IncentiveCategory[],
    }),
    [categoriesResponse],
  )

  const isEditModal = useMatch('/campaigns/incentives/:id')?.pathname.includes(
    '/campaigns/incentives/',
  )

  // Customer state
  const [customerId, setCustomerId] = useState<string>(
    incentiveById?.customerId ?? customersData?.customers?.[0]?.id ?? '',
  )

  // Track selected type for schema validation
  const [selectedType, setSelectedType] = useState<string>(
    incentiveById?.type ?? '',
  )

  const hasIntersolveIntegration = useMemo(
    () => customerHasIntersolve(customersData?.customers, customerId),
    [customersData?.customers, customerId],
  )

  // Determine if editing based on incentive ID
  const isEditing = !!incentiveById?.id

  // Determine schema based on form context - use correct schema for create vs edit
  // Memoize with stable dependencies to prevent hot reloading
  const formSchema = useMemo(() => {
    const isLotteryRequired = false // Lottery campaigns require SKU code
    const isIntersolve =
      hasIntersolveIntegration && selectedType === 'intersolve' // Intersolve requires integrationId

    // Use appropriate schema: update requires id field, create doesn't
    return isEditing
      ? updateIncentiveItemFormSchema(isLotteryRequired, isIntersolve)
      : createIncentiveItemFormSchema(isLotteryRequired, isIntersolve)
  }, [isEditing, hasIntersolveIntegration, selectedType])

  // Initialize form with react-hook-form
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    control,
    formState: { errors },
  } = useForm<IncentiveItemFormType>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit', // Only validate on submit, not on blur
    defaultValues: {
      name: '',
      price: '',
      description: '',
      categoryId: '',
      type: '',
      skuCode: '',
      digitalCode: '',
      integrationId: '',
      isActive: false,
      picture: '',
      voucherCodesFilePath: '',
      customerId: '',
      giftGroupItemIds: [],
    },
  })

  const giftGroupItemIds = watch('giftGroupItemIds') ?? []

  // Watch form type changes and sync with state
  const watchedType = watch('type')
  useEffect(() => {
    if (watchedType !== selectedType) {
      setSelectedType(watchedType)
    }
  }, [watchedType, selectedType])

  // When the customer changes, reset any form state tied to the previous
  // customer's inventory:
  //   - giftGroupItemIds — always cleared (children belong to the old
  //     customer, server-side guard would reject them anyway).
  //   - integrationId   — always cleared. Intersolve shops are
  //     customer-scoped; the previous customer's shop is meaningless under
  //     the new customer and would orphan the reference if persisted. The
  //     server has a matching guard (`assertIntegrationBelongsToCustomer`)
  //     for forced-API callers.
  //   - type / selectedType — additionally cleared if the current type
  //     requires INTERSOLVE and the new customer has none, so the form
  //     cannot be submitted in an inconsistent state.
  const handleCustomerChange = useCallback(
    (nextCustomerId: string) => {
      setCustomerId(nextCustomerId)
      setValue('giftGroupItemIds', [], { shouldValidate: false })
      setValue('integrationId', '', { shouldValidate: false })

      const currentType = getValues('type')
      const typeNeedsIntersolve =
        currentType === 'intersolve' || currentType === 'gift_group'
      if (
        typeNeedsIntersolve &&
        !customerHasIntersolve(customersData?.customers, nextCustomerId)
      ) {
        setValue('type', '', { shouldValidate: false })
        setSelectedType('')
      }
    },
    [customersData?.customers, getValues, setValue],
  )

  // Navigation helper - use React Router navigate for client-side navigation
  // This preserves the React Query cache so invalidation works
  // If onCloseModal is provided (modal context), close the modal instead of navigating away
  const navigateTo = useCallback(() => {
    if (onCloseModal) {
      onCloseModal()
    } else {
      navigate('/incentives')
    }
  }, [onCloseModal, navigate])

  // Mutations
  type CreatePayload = z.infer<typeof CreateIncentiveItemApiSchema>
  type UpdatePayload = z.infer<typeof UpdateIncentiveItemApiSchema>

  const createMutation = useMutation({
    mutationFn: (data: {
      id?: string
      incentiveItem: Record<string, unknown>
    }) =>
      incentiveClient.create
        .mutate({
          query: { campaignId: id },
          body: data.incentiveItem as CreatePayload,
        })
        .then(throwIfNotOk),
    onSuccess: async (_, variables) => {
      await invalidateIncentiveQueries(queryClient, {
        customerId:
          (variables.incentiveItem.customerId as string) || customerId,
        categoryId: variables.incentiveItem.categoryId as string,
        campaignId: id,
      })
      navigateTo()
    },
  })

  const updateMutation = useMutation({
    mutationFn: (data: {
      id: string
      incentiveItem: Record<string, unknown>
    }) =>
      incentiveClient.update
        .mutate({
          params: { id: data.incentiveItem.id as string },
          query: { campaignId: id },
          body: data.incentiveItem as UpdatePayload,
        })
        .then(throwIfNotOk),
    onSuccess: async (_, variables) => {
      await invalidateIncentiveQueries(queryClient, {
        customerId:
          (variables.incentiveItem.customerId as string) || customerId,
        categoryId: variables.incentiveItem.categoryId as string,
        campaignId: id,
        incentiveId: variables.incentiveItem.id as string,
      })
      navigateTo()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (incentiveId: string) =>
      incentiveClient.delete.mutate({
        params: { id: incentiveId },
      }),
    onSuccess: (_, incentiveId) => {
      invalidateIncentiveQueries(queryClient, {
        customerId: customerId,
        categoryId: incentiveById?.categoryId,
        campaignId: id,
        incentiveId,
      })
      navigateTo()
    },
  })

  // Load incentive data into form
  useEffect(() => {
    if (incentiveById) {
      // Set id for update schema validation
      if (incentiveById.id) {
        setValue('id', incentiveById.id)
      }
      setValue('name', incentiveById.name ?? '')
      setValue('price', incentiveById.price?.toString() ?? '')
      setValue('description', incentiveById.description || '')
      setValue('categoryId', incentiveById.categoryId || '')
      setValue('type', incentiveById.type || '')
      setValue('skuCode', incentiveById.skuCode || '')
      setValue('digitalCode', incentiveById.digitalCode || '')
      setValue('integrationId', incentiveById.integrationId || '')
      setValue('isActive', incentiveById.isActive ?? false)
      setValue('picture', incentiveById.picture || '')
      setValue('voucherCodesFilePath', incentiveById.voucherCodesFilePath || '')
      setCustomerId(incentiveById.customerId ?? '')
      setSelectedType(incentiveById.type || '')
      // Load gift group item IDs for gift_group type
      if (incentiveById.groupChildren?.length) {
        setValue(
          'giftGroupItemIds',
          incentiveById.groupChildren.map(
            (item: { childItemId: string }) => item.childItemId,
          ),
        )
      }
    }
  }, [incentiveById, setValue])

  // Set default customer
  useEffect(() => {
    if (customersData?.customers?.[0]?.id && !customerId) {
      setCustomerId(customersData.customers[0].id)
    }
  }, [customersData, customerId])

  // Load incentive by ID
  useEffect(() => {
    if (urlIncentiveId && incentiveId) {
      getIncentiveById()
    }
  }, [urlIncentiveId, incentiveId, getIncentiveById])

  // Delete handler
  const onClickDelete = () => {
    if (!incentiveById?.id) return

    deleteMutation.mutate(incentiveById.id, {
      onError: (error: Error) => {
        try {
          const parsedError = JSON.parse(error.message) as {
            message: string
            numberOfCampaigns: string
          }
          setErrorMessage({
            title: t('incentive_errors_modal.delete_error_modal_title', {
              numberOfCampaigns: parsedError?.numberOfCampaigns,
            }),
            text: t(`incentive_errors_modal.${parsedError?.message}`, {
              numberOfCampaigns: parsedError?.numberOfCampaigns,
            }),
          })
        } catch {
          setErrorMessage({
            title: t('error'),
            text: error?.message || t('unknown_error'),
          })
        }
        setShowErrorModal(true)
      },
    })
  }

  // Form submission
  const onSubmit = useCallback(
    async (data: IncentiveItemFormType) => {
      const integrationId =
        hasIntersolveIntegration && selectedType === 'intersolve'
          ? data.integrationId
          : null

      // Transform form data to API format
      // Note: Backend schema has z.preprocess for price, but we convert here for clarity
      const payload = {
        ...data,
        price:
          typeof data.price === 'string' ? parseFloat(data.price) : data.price,
        type: data.type || 'physical', // Default to physical if empty (fallback, should not happen with validation)
        integrationId,
        customerId:
          user?.user?.role === 'Superadmin'
            ? customerId ?? null
            : user?.user?.customerId,
        // Ensure optional fields are either string or null (not undefined)
        skuCode: data.skuCode || null,
        digitalCode: data.digitalCode || null,
        voucherCodesFilePath: data.voucherCodesFilePath || null,
        giftGroupItemIds:
          selectedType === 'gift_group' ? data.giftGroupItemIds ?? [] : null,
      } as const

      const handleError = (error: Error) => {
        try {
          const parsedError = JSON.parse(error.message) as {
            error?: string
            message?: string
            numberOfCampaigns?: string
          }
          // Server guards return { error, message }. `error` is the i18n key,
          // in the root namespace — the same convention campaign/list.tsx uses
          // with t(body.error). Older throws on this route carry only
          // `message`, keyed under incentive_errors_modal.
          const key = parsedError.error
            ? parsedError.error
            : `incentive_errors_modal.${parsedError?.message}`
          const translated = t(key, {
            numberOfCampaigns: parsedError?.numberOfCampaigns,
          })
          setErrorMessage({
            title: t(
              incentiveById?.id
                ? 'incentive_errors_modal.update_error_modal_title'
                : 'incentive_errors_modal.create_error_modal_title',
              { numberOfCampaigns: parsedError?.numberOfCampaigns },
            ),
            // i18next returns the key itself when there is no entry. Show the
            // server's own sentence rather than a raw dotted string.
            text:
              translated === key
                ? parsedError.message || t('unknown_error')
                : translated,
          })
        } catch {
          setErrorMessage({
            title: t('error'),
            text: error?.message || t('unknown_error'),
          })
        }
        setShowErrorModal(true)
      }

      if (incentiveById?.id) {
        updateMutation.mutate(
          { id: id ?? '', incentiveItem: { ...payload, id: incentiveById.id } },
          { onError: handleError },
        )
      } else {
        createMutation.mutate(
          { id: id ?? '', incentiveItem: payload },
          { onError: handleError },
        )
      }
    },
    [
      hasIntersolveIntegration,
      selectedType,
      incentiveById?.id,
      id,
      createMutation,
      updateMutation,
      t,
      user?.user?.customerId,
      user?.user?.role,
      customerId,
    ],
  )

  // Loading states
  if (
    loadingIncentive ||
    categoriesLoading ||
    loadingUser ||
    customersLoading
  ) {
    return <div>{t('loading')}</div>
  }

  const isClaimed = incentiveById?.isClaimed ?? false
  const isSuperadmin = user?.user?.role === 'Superadmin'
  const canEdit =
    isSuperadmin || user?.user?.customerId === incentiveById?.customerId

  return (
    <div className="p-8 space-y-2 flex flex-col max-w-2xl m-auto">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Header */}
        {isEditModal ? (
          <>
            <h1 className="text-lg text-gray-9 font-bold">
              {t('add_incentive.add_incentive')}
            </h1>
            <h2 className="text-sm text-gray-10">
              {t('add_incentive.upload_here')}
            </h2>
          </>
        ) : (
          <div className="flex gap-x-2 align-middle mb-8 items-center">
            <button type="button" onClick={navigateTo}>
              <ArrowLeft
                width={44}
                height={24}
                className="items-start flex -ml-2"
              />
            </button>
            {incentiveById?.id ? (
              <div className="grid">
                <div className="flex gap-x-2 items-center">
                  <h1 className="mb-0">{incentiveById.name}</h1>
                  <div
                    className={cx(
                      incentiveById.isActive ? 'bg-primary' : 'bg-ui',
                      'w-2 h-2 rounded-full',
                    )}
                  />
                </div>
                <p className="text-gray-200 text-xs">
                  {incentiveById?.sku ? `SKU: ${incentiveById.sku}` : ''}
                </p>
              </div>
            ) : (
              <h1 className="m-0">{t('incentives_store.add_incentive')}</h1>
            )}
          </div>
        )}

        {/* Picture Upload */}
        <div className="my-4">
          <CustomFileUpload
            name="picture"
            control={control}
            uploadUrl={`/upload/incentive-item?incentiveItemId=${incentiveId}`}
            allowedFileTypes={['jpg', 'png']}
            testId="incentive-image-upload"
          />
          <p className="mt-2 text-xs text-gray-10">
            {t('add_incentive.upload_image_hint')}
          </p>
        </div>

        {/* Name and Price */}
        <div className="flex gap-4 my-4">
          <FormInput
            name="name"
            control={control}
            label={t('add_incentive.name')}
            type="text"
            wrapperClassName="flex-1 pr-2"
            inputClassName="border rounded px-3 py-2 w-full"
            testId="incentive-name"
          />

          <FormInput
            name="price"
            control={control}
            label={t('add_incentive.price')}
            type="number"
            step="0.01"
            wrapperClassName="flex-1"
            inputClassName="border rounded px-3 py-2 w-full"
            testId="incentive-price"
          />
        </div>

        {/* Customer Select (Superadmin only) */}
        {isSuperadmin && (
          <div className="flex flex-col gap-1 my-4">
            <label htmlFor="customerId">{t('add_incentive.customer')}</label>
            <select
              id="customerId"
              value={customerId}
              onChange={(e) => handleCustomerChange(e.target.value)}
              disabled={isClaimed}
              className="border rounded px-3 py-2 w-full"
              data-testid="incentive-customer-select"
            >
              {customersData?.customers?.map((customer: Customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>
            {isClaimed && (
              <span className="text-gray-10 text-xs">
                {t('add_incentive.locked_after_claim')}
              </span>
            )}
          </div>
        )}

        {/* Description */}
        <div className="flex flex-col gap-1 my-4">
          <label>{t('add_incentive.description')}</label>
          <CustomTextarea
            name="description"
            control={control}
            testId="incentive-description"
            // maxLength={700}
          />
        </div>

        {/* Category */}
        <div className="flex flex-col gap-1 my-4">
          <label htmlFor="categoryId">{t('add_incentive.category')}</label>
          <select
            id="categoryId"
            {...register('categoryId')}
            className="border rounded px-3 py-2 w-full"
            data-testid="incentive-category"
          >
            <option value="">Select a category</option>
            {categoriesData?.incentiveCategories?.map(
              (category: IncentiveCategory) => (
                <option key={category.id} value={category.id}>
                  {t(`incentive_categories.${category.name}`)}
                </option>
              ),
            )}
          </select>
          {errors.categoryId && (
            <span className="text-primary">
              {t('validation_messages.required')}
            </span>
          )}
        </div>

        {/* Type */}
        <div className="flex flex-col gap-1 my-4 mb-4">
          <label>{t('add_incentive.type')}</label>
          <RadioGroupWithDescription
            name="type"
            control={control}
            options={incentiveTypeOptions(hasIntersolveIntegration)}
            disabled={isClaimed}
            testId="campaign-category"
          />
          {isClaimed && (
            <span className="text-gray-10 text-xs">
              {t('add_incentive.locked_after_claim')}
            </span>
          )}
          {errors.type && (
            <span className="text-primary">
              {t('validation_messages.required')}
            </span>
          )}
        </div>

        {/* SKU Code */}
        <FormInput
          name="skuCode"
          control={control}
          label={t('incentives.sku_code')}
          type="text"
          wrapperClassName="my-4"
          inputClassName="border rounded px-3 py-2 w-full bg-gray-100"
          disabled={true}
          testId="incentive-sku-code"
        />

        {/* Digital Code (for universal type) */}
        {selectedType === 'universal' && (
          <FormInput
            name="digitalCode"
            control={control}
            label={t('incentives.digital_code')}
            type="text"
            wrapperClassName="my-4"
            inputClassName="border rounded px-3 py-2 w-full"
            disabled={incentiveById?.isAttachedToLotteryCampaign ?? false}
            testId="incentive-digital-code"
          />
        )}

        {/* Unique type - voucher codes upload */}
        {selectedType === 'unique' && (
          <>
            <div className="flex flex-row cursor-pointer mt-6 justify-between items-center">
              <div>{t('add_incentive.upload_voucher_code_file')}</div>
              <div className="flex">
                <Download className="self-center mr-2" />
                <a
                  className="text-primary"
                  href="/download/example-voucher-codes.csv"
                  download={true}
                >
                  {t('add_incentive.download_example')}
                </a>
              </div>
            </div>

            <div className="my-4">
              <CustomFileUpload
                name="voucherCodesFilePath"
                control={control}
                uploadUrl={
                  incentiveId
                    ? `/upload/incentive-item-codes?incentiveItemId=${incentiveId}`
                    : '/upload/incentive-item-codes'
                }
                allowedFileTypes={['csv', 'xls', 'xlsx']}
                testId="voucher-codes-upload"
              />
            </div>

            {/* Uploaded files display */}
            {incentiveById?.files && incentiveById.type === 'unique' && (
              <>
                <h1 className="my-4">{t('incentives_store.uploaded_files')}</h1>
                {incentiveById.files
                  ?.filter(
                    (file: VoucherFile) => file?.fileType === 'VOUCHER_CODES',
                  )
                  ?.filter((file: VoucherFile) => !!file)
                  ?.map((file: VoucherFile, index: number) => {
                    if (!file) return null
                    return (
                      <div
                        key={index}
                        className="flex flex-row gap-2 p-4 border border-gray-500 rounded-md items-center justify-between my-2"
                      >
                        <div className="flex gap-4">
                          <FileCsv size={26} className="self-center" />
                          {file.s3Key}
                        </div>
                        <div className="flex gap-4">
                          <a href={file.url?.toString()}>
                            <DownloadSimple size={24} />
                          </a>
                        </div>
                      </div>
                    )
                  })}
              </>
            )}
          </>
        )}

        {/* Intersolve type - shop settings */}
        {selectedType === 'intersolve' && hasIntersolveIntegration && (
          <div className="my-2 space-y-1">
            <div>{t('add_incentive.shop')}</div>
            <IntersolveSettings
              onChange={(value) => setValue('integrationId', value)}
              customerId={customerId}
              disabled={isClaimed}
              value={watch('integrationId') ?? undefined}
            />
            {errors.integrationId && (
              <span className="text-primary text-sm">
                {t('validation_messages.required')}
              </span>
            )}
          </div>
        )}

        {/* Gift Group type - select intersolve incentive items */}
        {selectedType === 'gift_group' && hasIntersolveIntegration && (
          <div className="my-2 space-y-1">
            <div>{t('add_incentive.gift_group_items')}</div>
            <GiftGroupSettings
              selected={giftGroupItemIds}
              onChange={(valueOrUpdater) => {
                const next =
                  typeof valueOrUpdater === 'function'
                    ? valueOrUpdater(giftGroupItemIds)
                    : valueOrUpdater
                setValue('giftGroupItemIds', next, { shouldValidate: true })
              }}
              customerId={customerId}
            />
            {errors.giftGroupItemIds?.message && (
              <p className="text-primary text-sm">
                {t(
                  `add_incentive.${errors.giftGroupItemIds.message as string}`,
                )}
              </p>
            )}
          </div>
        )}

        {/* Hidden integrationId field */}
        <input type="hidden" {...register('integrationId')} />

        {/* Status */}
        <div className="grid my-4">
          <p className="pb-4">{t('incentives_store.status')}</p>
          <div className="flex w-full bg-gray-8 rounded-md p-4 items-center">
            <CustomSwitchComponent
              name="isActive"
              control={control}
              onCheckedChange={(value) => {
                const featured = incentiveById?.incentiveAttachedToCampaign ?? 0
                if (!value && featured > 0) setShowDisableModal(true)
                else setValue('isActive', value)
              }}
            />
            <div className="px-4">{t('incentives_store.active')}</div>
          </div>
        </div>

        {/* Errors */}
        {(createMutation.error || updateMutation.error) && (
          <p className="text-primary">
            {t(
              createMutation.error
                ? `${(createMutation.error as Error)?.message}`
                : `${(updateMutation.error as Error)?.message}`,
              { sku: watch('skuCode') },
            )}
          </p>
        )}

        {/* Action Buttons */}
        <div
          className={cx(
            incentiveById?.id ? 'flex justify-between' : 'flex justify-end',
            'py-8',
          )}
        >
          {incentiveById?.id && (
            <Button
              type="button"
              name="delete"
              label={t('delete')}
              size="small"
              IconLeft={Trash}
              onClick={() => {
                const featured = incentiveById?.incentiveAttachedToCampaign
                setShowErrorModal(true)
                setErrorMessage({
                  title: featured
                    ? t('incentive_errors_modal.delete_error_modal_title', {
                        numberOfCampaigns: featured,
                      })
                    : t('are you sure you want to delete this incentive?'),
                  text: featured
                    ? t(
                        'incentive_errors_modal.delete_incentive_item_is_used',
                        { numberOfCampaigns: featured },
                      )
                    : t('are you sure you want to delete this incentive?'),
                })
              }}
              disabled={!canEdit || isClaimed}
              data-testid="incentive-delete"
            />
          )}
          <div className="grid grid-cols-2 gap-4 justify-end items-end">
            <Button
              type="button"
              name="cancel"
              label={t('cancel')}
              size="small"
              onClick={navigateTo}
              data-testid="incentive-cancel"
            />
            <Button
              primary={true}
              type="submit"
              name="add-incentive"
              label={incentiveById?.id ? t('save') : t('incentives_store.add')}
              size="small"
              data-testid="incentive-submit"
              disabled={!canEdit}
            />
          </div>
        </div>
      </form>

      {/* Error Modal */}
      {showErrorModal &&
        createPortal(
          <ErrorModal
            title={errorMessage?.title ?? ''}
            error={errorMessage?.text ?? ''}
            onClick={() => setShowErrorModal(false)}
            hasOnSubmit={!isClaimed}
            onSubmit={onClickDelete}
            submitButtonText={t('delete')}
          />,
          document.body,
        )}

      {/* Disable confirmation: turning off an incentive that's live in
          campaigns would pull it from those end-user flows. */}
      {showDisableModal &&
        createPortal(
          <ErrorModal
            title={t('incentive_errors_modal.update_error_modal_title', {
              numberOfCampaigns: incentiveById?.incentiveAttachedToCampaign,
            })}
            error={t('incentive_errors_modal.update_incentive_item_is_used', {
              numberOfCampaigns: incentiveById?.incentiveAttachedToCampaign,
            })}
            onClick={() => setShowDisableModal(false)}
            hasOnSubmit={true}
            onSubmit={() => {
              setValue('isActive', false)
              setShowDisableModal(false)
            }}
            submitButtonText={t('disable')}
          />,
          document.body,
        )}
    </div>
  )
}
